<template>
<div class="ranking-question">
  <div>
    <img v-if="node.question.image !== null" :src="node.question.image" class="question-image" />
    <p v-if="node.prompt !== null" v-html="node.prompt"></p>
    <div v-html="node.question.instructions"></div>

    <!-- STAGE 1: Select top N (two-stage only) -->
    <div v-if="isTwoStage && stage === 'select'" class="ranking-area">
      <div class="stage-indicator">
        <span class="stage-dot active">1</span>
        <span class="stage-connector"></span>
        <span class="stage-dot inactive">2</span>
        <span class="stage-connector"></span>
        <span class="stage-dot inactive">3</span>
        <span class="stage-text">Select your top {{ selectLimit }}</span>
      </div>

      <p class="stage-hint">
        Tap to select your top {{ selectLimit }}.
        Don't worry about order yet — you'll rank them next.
      </p>

      <div class="section-label">
        Selected
        <span class="count">{{ selectedIds.length }} / {{ selectLimit }}</span>
      </div>

      <div
        v-for="opt in filteredOptionList"
        :key="'select-' + opt.id"
        class="option-card"
        :class="{
          selected: isSelected(opt.id),
          'at-max': atSelectionMax && !isSelected(opt.id),
        }"
        @click="toggleSelect(opt.id)"
      >
        <div v-if="isSelected(opt.id)" class="check-badge">&#10003;</div>
        <div v-else class="empty-badge"></div>
        <div class="option-label">
          <img
            v-if="opt.image != null"
            :src="opt.image"
            :alt="opt.label"
            class="option-image"
          />
          <span>{{ opt.label }}</span>
        </div>
      </div>
    </div>

    <!-- STAGE 2: Rank -->
    <div v-if="stage === 'rank'" class="ranking-area">
      <div v-if="isTwoStage" class="stage-indicator">
        <span class="stage-dot complete">&#10003;</span>
        <span class="stage-connector complete"></span>
        <span class="stage-dot active">2</span>
        <span class="stage-connector"></span>
        <span class="stage-dot inactive">3</span>
        <span class="stage-text">Rank your selections</span>
      </div>

      <div class="section-label">
        Your Ranking
        <span class="count">{{ rankedIds.length }} / {{ selectLimit }}</span>
      </div>

      <div v-if="rankedIds.length === 0" class="empty-ranked">
        Tap an option below to start ranking
      </div>

      <div>
        <div
          v-for="(id, index) in rankedIds"
          v-if="options[id]"
          :key="'ranked-' + id"
          class="option-card ranked"
          :class="{ 'swap-flash': swappedIds.includes(id) }"
        >
          <div class="rank-badge">{{ index + 1 }}</div>
          <div class="option-label">
            <img
              v-if="options[id].image != null"
              :src="options[id].image"
              :alt="options[id].label"
              class="option-image"
            />
            <span>{{ options[id].label }}</span>
          </div>
          <div class="arrow-btns">
            <button
              class="arrow-btn"
              :disabled="index === 0"
              @click.stop="moveUp(index)"
            ><span class="chevron up"></span></button>
            <button
              class="arrow-btn"
              :disabled="index === rankedIds.length - 1"
              @click.stop="moveDown(index)"
            ><span class="chevron down"></span></button>
          </div>
          <button class="unrank-btn" @click.stop="unrankOption(index)">&times;</button>
        </div>
      </div>

      <div v-if="unrankedOptions.length > 0" class="ranking-divider"></div>

      <div v-if="unrankedOptions.length > 0" class="section-label">Choose Next</div>
      <div
        v-for="opt in unrankedOptions"
        :key="'unranked-' + opt.id"
        class="option-card"
        @click="rankOption(opt.id)"
      >
        <div class="empty-badge"></div>
        <div class="option-label">
          <img
            v-if="opt.image != null"
            :src="opt.image"
            :alt="opt.label"
            class="option-image"
          />
          <span>{{ opt.label }}</span>
        </div>
      </div>
    </div>

    <!-- STAGE 3: Review / Confirm -->
    <div v-if="stage === 'review'" class="ranking-area">
      <div v-if="isTwoStage" class="stage-indicator">
        <span class="stage-dot complete">&#10003;</span>
        <span class="stage-connector complete"></span>
        <span class="stage-dot complete">&#10003;</span>
        <span class="stage-connector complete"></span>
        <span class="stage-dot active">3</span>
        <span class="stage-text">Confirm your ranking</span>
      </div>

      <div class="review-header">
        <p class="review-title">Review your choices below. This is what will be submitted.</p>
      </div>

      <div class="review-screen">
        <div
          v-for="(id, index) in rankedIds"
          v-if="options[id]"
          :key="'review-' + id"
          class="review-item"
        >
          <div class="review-rank">{{ index + 1 }}</div>
          <div class="review-label">{{ options[id].label }}</div>
        </div>
      </div>
    </div>
  </div>

  <div class="buttons">
    <!-- Stage 1: Next button -->
    <v-btn
      v-if="isTwoStage && stage === 'select'"
      class="submit-button"
      :disabled="!isSelectionComplete"
      color="primary"
      @click="goToRankStage"
    >
      Next: Rank Your Top {{ selectLimit }}
    </v-btn>

    <!-- Stage 2: Review button -->
    <v-btn
      v-if="stage === 'rank'"
      class="submit-button"
      :disabled="!isRankingComplete"
      color="primary"
      @click="goToReviewStage"
    >
      Review My Ranking
    </v-btn>
    <v-btn
      v-if="isTwoStage && stage === 'rank'"
      class="submit-button"
      color="secondary"
      @click="goBackToSelectStage"
    >
      &larr; Change Selections
    </v-btn>

    <!-- Stage 3: Submit + Edit buttons -->
    <v-btn
      v-if="stage === 'review'"
      class="submit-button"
      color="primary"
      @click="onAnswerQuestion"
    >
      Submit Ranking
    </v-btn>
    <v-btn
      v-if="stage === 'review'"
      class="submit-button"
      color="secondary"
      @click="goBackToRankStage"
    >
      &larr; Edit Ranking
    </v-btn>

    <!-- Skip (available on all stages if skippable) -->
    <v-btn
      v-if="node.skippable"
      class="submit-button"
      color="secondary"
      @click="onSkipQuestion"
    >
      {{ skipButton }}
    </v-btn>
  </div>
</div>
</template>

<script>
import optionFilterMixin from '../mixins/optionFilterMixin';

export default {
  name: 'RankingQuestion',
  mixins: [optionFilterMixin],
  data() {
    const options = {};
    const optionList = [];

    this.node.question.options.forEach((item) => {
      options[item.id] = item;
      optionList.push(item);
    });

    const rankCount = this.node.question.rank_count || 0;
    const isTwoStage = rankCount > 0 && rankCount < optionList.length;

    return {
      options,
      optionList,
      stage: isTwoStage ? 'select' : 'rank',
      selectedIds: [],
      rankedIds: [],
      swappedIds: [],
    };
  },
  beforeDestroy() {
    if (this.flashTimer) clearTimeout(this.flashTimer);
  },
  computed: {
    filteredOptionList() {
      return this.getFilteredOptions(this.node, this.optionList);
    },
    rankCount() {
      return this.node.question.rank_count || 0;
    },
    isTwoStage() {
      return this.rankCount > 0 && this.rankCount < this.filteredOptionList.length;
    },
    selectLimit() {
      return this.isTwoStage ? this.rankCount : this.filteredOptionList.length;
    },
    selectPool() {
      if (!this.isTwoStage) {
        return this.filteredOptionList;
      }
      const selectedSet = new Set(this.selectedIds);
      return this.filteredOptionList.filter((opt) => selectedSet.has(opt.id));
    },
    rankedOptions() {
      return this.rankedIds.map((id) => this.options[id]);
    },
    unrankedOptions() {
      const rankedSet = new Set(this.rankedIds);
      return this.selectPool.filter((opt) => !rankedSet.has(opt.id));
    },
    isSelectionComplete() {
      return this.selectedIds.length === this.selectLimit;
    },
    isRankingComplete() {
      return this.rankedIds.length === this.selectLimit;
    },
    atSelectionMax() {
      return this.selectedIds.length >= this.selectLimit;
    },
  },
  methods: {
    // Stage 1
    isSelected(id) {
      return this.selectedIds.includes(id);
    },
    toggleSelect(id) {
      if (this.isSelected(id)) {
        this.selectedIds = this.selectedIds.filter((sid) => sid !== id);
      } else if (!this.atSelectionMax) {
        this.selectedIds.push(id);
      }
    },
    goToRankStage() {
      this.rankedIds = [];
      this.stage = 'rank';
    },
    goBackToSelectStage() {
      this.rankedIds = [];
      this.stage = 'select';
    },

    // Stage 2
    flashSwapped(idA, idB) {
      // Clear first to restart animation if already running
      this.swappedIds = [];
      if (this.flashTimer) {
        clearTimeout(this.flashTimer);
      }
      this.$nextTick(() => {
        this.swappedIds = [idA, idB];
        this.flashTimer = setTimeout(() => { this.swappedIds = []; }, 800);
      });
    },
    moveUp(index) {
      if (index <= 0) return;
      const idA = this.rankedIds[index];
      const idB = this.rankedIds[index - 1];
      this.$set(this.rankedIds, index, idB);
      this.$set(this.rankedIds, index - 1, idA);
      this.flashSwapped(idA, idB);
    },
    moveDown(index) {
      if (index >= this.rankedIds.length - 1) return;
      const idA = this.rankedIds[index];
      const idB = this.rankedIds[index + 1];
      this.$set(this.rankedIds, index, idB);
      this.$set(this.rankedIds, index + 1, idA);
      this.flashSwapped(idA, idB);
    },
    rankOption(id) {
      this.rankedIds.push(id);
    },
    unrankOption(index) {
      this.rankedIds.splice(index, 1);
    },
    goToReviewStage() {
      this.stage = 'review';
    },
    goBackToRankStage() {
      this.stage = 'rank';
    },

    // Stage 3
    // id/value = top-ranked option (for nodelink compatibility); values/text = full ranking
    onAnswerQuestion() {
      const sorted = this.rankedIds.map((id) => this.options[id]).filter(Boolean);
      if (sorted.length === 0) return;

      const values = sorted.map((opt) => opt.value).join('|');
      const text = sorted.map((opt) => opt.label).join('|');

      this.$emit('answer', {
        id: sorted[0].id,
        value: sorted[0].value,
        text,
        values,
        node: this.node,
      });

      this.resetState();
    },
    onSkipQuestion() {
      this.$emit('skip');
      this.resetState();
    },
    resetState() {
      this.selectedIds = [];
      this.rankedIds = [];
      this.stage = this.isTwoStage ? 'select' : 'rank';
    },
  },
  props: {
    node: Object,
    submitButton: String,
    skipButton: String,
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/_variables.scss";

.ranking-question {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding: 1.5rem;

  .question-image {
    display: block;
    max-width: 340px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 2rem;
  }

  .ranking-area {
    margin-top: 1rem;
  }

  // Stage indicator
  .stage-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-primary-base, #1976d2);
  }

  .stage-dot {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;

    &.active {
      background: var(--v-primary-base, #1976d2);
      color: #fff;
    }

    &.inactive {
      background: #e0e0e0;
      color: #999;
    }

    &.complete {
      background: #4CAF50;
      color: #fff;
    }
  }

  .stage-connector {
    width: 24px;
    height: 2px;
    background: #e0e0e0;

    &.complete {
      background: #4CAF50;
    }
  }

  .stage-text {
    margin-left: 4px;
  }

  .stage-hint {
    font-size: 13px;
    color: #888;
    margin: 0 0 12px;
    line-height: 1.4;
  }

  // Section labels
  .section-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--v-primary-base, #1976d2);
    margin: 12px 0 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    .count {
      background: var(--v-primary-base, #1976d2);
      color: #fff;
      border-radius: 10px;
      padding: 1px 8px;
      font-size: 11px;
      font-weight: 600;
    }
  }

  // Option cards
  .option-card {
    display: flex;
    align-items: flex-start;
    background: #fff;
    border: 2px solid #e8e8e8;
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    -webkit-user-select: none;
    min-height: 56px;

    &:not(.ranked):active {
      transform: scale(0.98);
    }

    &.ranked {
      border-color: var(--v-primary-base, #1976d2);
      background: #f5f8ff;
      cursor: default;
    }

    &.selected {
      border-color: #4CAF50;
      background: #F1F8E9;
    }

    &.at-max {
      opacity: 0.4;
      pointer-events: none;
    }
  }

  // Badges
  .rank-badge {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--v-primary-base, #1976d2);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 15px;
    margin-right: 12px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .empty-badge {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px dashed #ccc;
    margin-right: 12px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .check-badge {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #4CAF50;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    margin-right: 12px;
    margin-top: 2px;
    flex-shrink: 0;
  }

  // Option label
  .option-label {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    flex: 1;
    min-width: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;
    padding-top: 4px;
    padding-right: 8px;
    word-break: break-word;

    .option-image {
      width: 60px;
      height: 60px;
      object-fit: cover;
      border-radius: 6px;
      margin-right: 1rem;
      flex-shrink: 0;
    }
  }

  // Reorder controls
  .arrow-btns {
    display: flex;
    flex-direction: row;
    gap: 4px;
    margin-left: 8px;
    flex-shrink: 0;

    .arrow-btn {
      width: 40px;
      height: 40px;
      border: 2px solid #ddd;
      border-radius: 50%;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.15s;
      padding: 0;

      .chevron {
        width: 12px;
        height: 12px;
        border-right: 2.5px solid #666;
        border-bottom: 2.5px solid #666;
        display: block;

        &.up {
          transform: rotate(-135deg);
          margin-top: 3px;
        }

        &.down {
          transform: rotate(45deg);
          margin-top: -3px;
        }
      }

      &:hover:not(:disabled) {
        background: #f0f0f0;
        border-color: var(--v-primary-base, #1976d2);

        .chevron {
          border-color: var(--v-primary-base, #1976d2);
        }
      }

      &:active:not(:disabled) {
        transform: scale(0.92);
      }

      &:disabled {
        opacity: 0.25;
        cursor: not-allowed;
      }
    }
  }

  .unrank-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #e57373;
    background: #fff;
    color: #e57373;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 12px;
    margin-top: 2px;
    flex-shrink: 0;
    transition: all 0.15s;
    padding: 0;

    &:hover {
      background: #e57373;
      color: #fff;
    }

    &:active {
      transform: scale(0.92);
    }
  }

  // Dividers and empty state
  .ranking-divider {
    height: 1px;
    background: #e8e8e8;
    margin: 12px 0;
  }

  .empty-ranked {
    text-align: center;
    padding: 20px;
    color: #bbb;
    font-size: 14px;
    border: 2px dashed #e8e8e8;
    border-radius: 12px;
    margin-bottom: 8px;
  }

  // Swap flash animation
  @keyframes swap-flash {
    0% { background: #bbdefb; }
    50% { background: #bbdefb; }
    100% { background: #f5f8ff; }
  }

  .option-card.swap-flash {
    animation: swap-flash 0.8s ease;
  }

  // Review screen
  .review-header {
    margin-bottom: 8px;
  }

  .review-title {
    font-size: 14px;
    color: #666;
    line-height: 1.4;
  }

  .review-screen {
    margin-bottom: 8px;
  }

  .review-item {
    display: flex;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }
  }

  .review-rank {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--v-primary-base, #1976d2);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16px;
    margin-right: 16px;
    flex-shrink: 0;
  }

  .review-label {
    font-size: 16px;
    font-weight: 500;
  }

  // Buttons
  .buttons {
    display: flex;
    width: 100%;
    flex-direction: row-reverse;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    align-self: center;
    margin-top: 1.5rem;
  }
}

@media (min-width: $breakpoint-tablet) {
  .ranking-question {
    justify-content: flex-start;

    .buttons {
      margin-top: 3rem;
    }
  }
}
</style>
